import { LoaderMask } from 'components/LoaderMask/LoaderMask';
import { useAsyncEffect } from 'customHooks/hooks';
import React, { ReactElement, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { WidgetGeneratorContext } from 'screens/WidgetGenerator/contexts/WidgetGeneratorContext';
import { getAnswersChoicesQuestionSummary } from 'screens/WidgetGenerator/helpers/answersChoicesQuestionSummary';
import { ChoicesQuestionChartDisplayOrder, ChoicesQuestionChartDisplayType } from 'screens/WidgetGenerator/utils/misc';
import { ChoicesQuestionChart } from 'screens/WidgetGenerator/widgets/ChoicesQuestionChart/ChoicesQuestionChart';
import { logger } from 'utils/logger';
import { ReportBuilderWidgetProps } from 'screens/WidgetGenerator/reportGeneratorComponents/types';

export const ChoicesQuestionChartHOC = (props: ReportBuilderWidgetProps): ReactElement => {
  const { params } = props;
  const selectedQuestion = params.filters?.choicesQuestionChartQuestionSelect;
  const isPercentageDisplaySelected = params.filters?.choicesQuestionChartDisplayType;
  const isPopularityOrderSelected = params.filters?.choicesQuestionChartOrderBy;
  const displayType = isPercentageDisplaySelected
    ? ChoicesQuestionChartDisplayType.PERCENTAGE
    : ChoicesQuestionChartDisplayType.INTEGER;
  const choicesOrder = isPopularityOrderSelected
    ? ChoicesQuestionChartDisplayOrder.POPULARITY
    : ChoicesQuestionChartDisplayOrder.APPEARANCE;

  const { client, cycles, selectedDateRange, selectedSurveyGroup } = useContext(WidgetGeneratorContext);

  const { t: translate } = useTranslation();

  const fetchChoicesQuestionData = async () => {
    if (!selectedQuestion?.value) {
      return null;
    }
    const selectedQuestionId = selectedQuestion?.value as number | undefined;

    const fetchedTopicsData = await getAnswersChoicesQuestionSummary({
      client,
      cycles,
      selectedDateRange,
      surveyGroupId: selectedSurveyGroup.id,
      genericQuestionId: selectedQuestionId,
    });
    return fetchedTopicsData;
  };

  const {
    fetchedData: choicesQuestionData,
    isLoading,
    error,
  } = useAsyncEffect(fetchChoicesQuestionData, [
    client,
    cycles,
    selectedDateRange,
    selectedQuestion,
    isPercentageDisplaySelected,
    selectedSurveyGroup,
  ]);

  if (error) {
    logger.error(error);
  }

  if (!choicesQuestionData) {
    return <p>{translate('widgetGenerator.widgets.choicesQuestionChart.emptyState')}</p>;
  }

  return (
    <LoaderMask isLoading={isLoading}>
      <ChoicesQuestionChart
        widgetData={choicesQuestionData}
        dataDisplayType={displayType}
        choicesOrder={choicesOrder}
      />
    </LoaderMask>
  );
};
